import { useEffect, useMemo, useRef, useState } from 'react';
import { Crop } from 'react-image-crop';
import { useCreativeTemplatesBuilderStore } from 'stores/CreativeTemplatesBuilderStore';
import { createBlobFromImageUrl } from '../utils';
import { ImageTypeEnum, UploadImages_uploadImages_creativeImages, useUploadImages } from 'api';
import { getErrorsText } from 'pages/Insights/components/Tabs/components/ErrorMessage';
import { Severity, usePushNotifications } from 'services/NotificationService';

export interface Image {
  file: File;
  crop?: Crop | undefined;
  croppedObjectUrl: string;
}

export const useImageUploader = (onClose: () => void) => {
  const notify = usePushNotifications();
  const store = useCreativeTemplatesBuilderStore();

  const styledDropzoneRef = useRef<HTMLImageElement | null>(null);

  const [images, setImages] = useState<Image[]>([]);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageIndex, setCropImageIndex] = useState<number | null>(null);
  const cropImage = useMemo(() => {
    return cropImageIndex !== null && images[cropImageIndex] ? images[cropImageIndex] : null;
  }, [images, cropImageIndex]);

  const [uploadImage, { loading }] = useUploadImages();

  const handleFilesSelect = (files: File[] | null, url?: string | undefined) => {
    if (url) return;
    if (!files) return;

    const selectedImages = files.map((file) => {
      return {
        file: file,
        croppedObjectUrl: URL.createObjectURL(file)
      };
    });

    setImages([...images, ...selectedImages]);
  };

  const handleFileDelete = (index: number) => {
    const newImages = images.filter((_, idx) => idx !== index);
    setImages(newImages);
  };

  const handleSave = async () => {
    if (!store.articleGroup) return;

    try {
      const blobs = await Promise.all(images.map((image) => createBlobFromImageUrl(image.croppedObjectUrl)));

      const { data } = await uploadImage({
        variables: { articleGroupId: Number(store.articleGroup.id), files: blobs }
      });

      if (!data || !data.uploadImages) {
        throw new Error('Invalid response from the server');
      }

      const { creativeImages, errors } = data.uploadImages;

      if (errors) {
        const errorMsg = getErrorsText(data?.uploadImages?.errors || []);
        throw new Error(errorMsg);
      }

      if (creativeImages) {
        const uploadedImages =
          creativeImages.map((image: UploadImages_uploadImages_creativeImages) => ({
            id: image!.id!,
            url: image!.url!,
            type: ImageTypeEnum.CreativeImage,
            imageType: image!.imageType!,
            ctr: image!.metrics?.ctr || 0,
            spent: image!.metrics?.spent || 0
          })) || [];

        store.setImages([...uploadedImages, ...store.images]);
        onClose();
        notify({ title: 'Success', message: 'Images were successfully uploaded !', severity: Severity.SUCCESS });
      }
    } catch (e) {
      notify({ title: 'Error', message: e.message, severity: Severity.ERROR });
    }
  };

  const triggerStyledDropzone = () => {
    // @ts-ignore
    styledDropzoneRef?.current?.openUploadDialog();
  };

  const handleSaveCrop = (crop: Crop | undefined, croppedObjectUrl: string | undefined) => {
    const newImages = images.map((image, index) => {
      if (index !== cropImageIndex) return image;

      return { ...image, crop: crop, croppedObjectUrl: croppedObjectUrl || URL.createObjectURL(image.file) };
    });

    setImages(newImages);
  };

  const handleCropButtonClick = (index: number) => {
    setCropImageIndex(index);
  };

  const handleCropDialogClose = () => {
    setCropImageIndex(null);
  };

  useEffect(() => {
    setCropDialogOpen(!!cropImage);
  }, [cropImage]);

  return {
    images,
    loading,

    styledDropzoneRef,
    triggerStyledDropzone,

    handleFilesSelect,
    handleFileDelete,
    handleSave,

    cropDialogOpen,
    cropImage,
    handleCropButtonClick,
    handleCropDialogClose,
    handleSaveCrop
  };
};
